export const NOTCHES = [0, 25, 50, 100, 250, 500, 1000];
export const NOTCH_LABELS = [
	'$0',
	'$25',
	'$50',
	'$100',
	'$250',
	'$500',
	'$1k+',
];

const SEGMENT_COUNT = NOTCHES.length - 1;

const clamp01 = (p: number) => Math.max(0, Math.min(1, p));

/**
 * The track is not linear in dollars: each gap between adjacent notches
 * ($0–$25, $25–$50, … $500–$1k) occupies an equal sixth of the track, so the
 * low end of the budget range gets fine-grained control. Values are rounded
 * to $5 so the live readout doesn't flicker through every dollar mid-drag.
 */
export function positionToValue(position: number): number {
	const scaled = clamp01(position) * SEGMENT_COUNT;
	const segment = Math.min(Math.floor(scaled), SEGMENT_COUNT - 1);
	const t = scaled - segment;
	const raw = NOTCHES[segment] + t * (NOTCHES[segment + 1] - NOTCHES[segment]);
	return Math.round(raw / 5) * 5;
}

/**
 * Inverse of positionToValue. Any value at or above the last notch pins the
 * thumb to the right end — the field can hold figures past $1,000, the track
 * can't.
 */
export function valueToPosition(value: number): number {
	if (value >= NOTCHES[SEGMENT_COUNT]) return 1;
	const v = Math.max(0, value);
	for (let i = 0; i < SEGMENT_COUNT; i++) {
		if (v <= NOTCHES[i + 1]) {
			const t = (v - NOTCHES[i]) / (NOTCHES[i + 1] - NOTCHES[i]);
			return (i + t) / SEGMENT_COUNT;
		}
	}
	return 1;
}

/**
 * Nearest notch in track space, for when the user releases the thumb: values
 * stay granular while dragging and settle on a marked stop on release. Typed
 * values are never snapped.
 */
export function snapToNotch(position: number): number {
	return Math.round(clamp01(position) * SEGMENT_COUNT) / SEGMENT_COUNT;
}

// Mirrors LADDER in beacon's shared/utils/marketing_budget.py —
// keep the two lists in sync (the cross-repo contract for ADO-1387).
export const TACTICS: { threshold: number; label: string }[] = [
	{ threshold: 0, label: 'Organic content, manual outreach, and email' },
	{ threshold: 25, label: 'A few small boosts to see what travels' },
	{ threshold: 50, label: "Consistent boosting to confirm what's working" },
	{ threshold: 100, label: 'Amplify proven posts + a small search-ad test' },
	{
		threshold: 250,
		label:
			'One bigger bet — a produced Reel, or a structured ad test, or a local push',
	},
	{
		threshold: 500,
		label: 'Real multi-channel testing across social, search, and offline',
	},
	{
		threshold: 1000,
		label:
			'Operate like a small brand — coordinated campaigns + freelance support',
	},
];

export type LadderRow = {
	label: string;
	threshold: number;
	state: 'active' | 'locked';
	isFrontier: boolean;
};

/**
 * The tactics rendered as a cumulative ladder: every tactic the budget reaches
 * is `active`, the rest are `locked`. The highest active row is the `frontier`
 * — the biggest move the budget just unlocked, which the UI emphasizes. At $0
 * the threshold-0 organic row is the (only) active row and the frontier sits on
 * it; at $1,000+ every row is active and the frontier sits on the top row.
 */
export function buildTacticLadder(budget: number): LadderRow[] {
	let frontierIndex = -1;
	for (let i = 0; i < TACTICS.length; i++) {
		if (TACTICS[i].threshold <= budget) frontierIndex = i;
	}
	return TACTICS.map((tactic, i) => ({
		label: tactic.label,
		threshold: tactic.threshold,
		state: tactic.threshold <= budget ? 'active' : 'locked',
		isFrontier: i === frontierIndex,
	}));
}

// At or above the top notch every tactic is in play — the "all in" cap.
export function isAllIn(budget: number): boolean {
	return budget >= NOTCHES[NOTCHES.length - 1];
}
